import React, {ReactElement, useEffect, useState} from "react";
import {Button, Container, Modal} from "semantic-ui-react";
import {SortColumn} from 'react-data-grid';
import {DataProvider} from "../../services/intf/DataProvider";
import {ListOptions} from "../../services/intf/ListOptions";
import AppListMenu, {AppListMenuButton} from "./AppListMenu";
import AppListDataGrid, {ColumnDefinition, DataGridSelection} from "./AppListDataGrid";
import AppListDeleteModal from "./AppListDeleteModal";
import PagingFooter from "./PagingFooter";
import {ListResultPaging} from "../../model/ListResult";
import {ReadDataProvider} from "../../services/intf/ReadDataProvider";

export enum AppListPageDialogContextState {
    add = "add",
    view = "view",
    edit = "edit",
    delete = "delete"
}

export interface AppListPageDialogContext<TItem> {
    state: AppListPageDialogContextState;
    model?: TItem;
    validateCallback?: () => Promise<boolean>;
    getResultCallback?: () => Promise<boolean>;

}

interface Props<TItem, TListOptions extends ListOptions> {
    provider: DataProvider<TItem, TListOptions> | ReadDataProvider<TItem, TListOptions>;
    //keyProperty: string;
    columns?: ColumnDefinition<TItem>[];
    getDetailsComponent?: (context: AppListPageDialogContext<TItem>) => ReactElement;
    newItemCreator?: () => TItem;
    menuButtons?: AppListMenuButton[],
    readOnly?: boolean,
    hideMenu?: boolean,
    hidePaging?: boolean,
    hideRefresh?: boolean,
    hideSearch?: boolean,
    listOptions?: TListOptions;
    multiSelect?: boolean;
    selectedIds?: string[];
    onSelectionChanged?: (selection: DataGridSelection) => void;
    getDisabledButtons?: (focusedItem?: TItem) => string[];
    getDetailsDialogTitle?: (state: AppListPageDialogContextState) => string;
    detailsItemTitleName?: string;
    onButtonClick?: (button: string, focusedItem?: TItem, reloadCallback?: () => void) => void;
    gridHeight?: string;
    onFilterChanged?: (filter: any | undefined) => void;
    onSortChanged?: (sort: string | undefined) => void;
}

const AppListPage = function <TItem, TListOptions extends ListOptions>(props: React.PropsWithChildren<Props<TItem, TListOptions>>) {

    function getInitialSortOptions(columns: ColumnDefinition<TItem>[] | undefined): string | undefined {
        if (columns) {
            const sortedColumn = columns.find((x) => x.sortDirection);
            if (sortedColumn) {
                return `${sortedColumn.sortDirection == "DESC" ? "-" : ""}${sortedColumn.key}`
            }
        }
        return undefined;
    }

    const [listOptions, setListOptions] = useState<TListOptions>(props.listOptions ?? {
        limit: 10,
        page: 1,
        sort: getInitialSortOptions(props.columns)
    } as TListOptions);
    const [filter, setFilter] = useState<any | undefined>();
    const [items, setItems] = useState<TItem[]>();
    const [paging, setPaging] = useState<ListResultPaging | undefined>();
    const [focusedItem, setFocusedItem] = useState<TItem>();
    const [dialogContext, setDialogContext] = useState<AppListPageDialogContext<TItem>>();
    const [disabledButtons, setDisabledButtons] = useState<string[]>(
        props.getDisabledButtons ? props.getDisabledButtons(undefined) :
            (props.readOnly ? ["add", "view", "edit", "delete"] : ["view", "edit", "delete"]));
    const [saving, setSaving] = useState<boolean>(false);
    const [loadDataError, setLoadDataError] = useState<string | undefined>();

    useEffect(() => {
        loadData().then();
    }, [listOptions]);

    useEffect(() => {
        if (props.getDisabledButtons) {
            setDisabledButtons(props.getDisabledButtons(focusedItem));
        } else {
            const newDisabledButtons: string[] = [];
            if (props.readOnly)
                newDisabledButtons.push("add");

            if (!focusedItem) {
                newDisabledButtons.push("view");
            }
            if (!focusedItem || props.readOnly) {
                newDisabledButtons.push("edit");
                newDisabledButtons.push("delete");
            }
            setDisabledButtons(newDisabledButtons);

        }
    }, [focusedItem]);

    function getDetailsDialogTitle(state: AppListPageDialogContextState) {
        const itemTitleName = props.detailsItemTitleName ?? "записи"
        switch (state) {
            case AppListPageDialogContextState.add:
                return `Добавление новой ${itemTitleName}`;
            case AppListPageDialogContextState.view:
                return `Просмотр ${itemTitleName}`;
            case AppListPageDialogContextState.edit:
                return `Редактирование ${itemTitleName}`;
        }
        return "?";
    }

    async function loadData() {
        setLoadDataError(undefined);
        try {
            let response = (await props.provider.list(listOptions));
            const id: any = focusedItem ? (focusedItem as any)[props.provider.keyProperty] : undefined;
            if ('paging' in response && response.paging) {
                await setItems(response.items);
                await setPaging(response.paging);
            } else {
                await setItems(response as TItem[]);
            }
            let newFocusedItem: any;
            if (id && items)
                newFocusedItem = items!.find((x: any) => x[props.provider.keyProperty] == id);
            setFocusedItem(newFocusedItem);
        } catch (e) {
            setLoadDataError(e?.toString() ?? "unknown error");
        }
    }

    function load(page?: number, limit?: number) {
        setListOptions({...listOptions, page: page ?? listOptions.page, limit: limit ?? listOptions.limit});
    }

    function onSearchChanged(value?: string) {
        setListOptions({...listOptions, search: value, page: 1});
    }

    function onSortColumnsChanged(sortColumns: SortColumn[]) {
        const newSort = sortColumns
            .map((x) => (x.direction == "DESC" ? "-" : "") + x.columnKey)
            .join(",");
        setListOptions({
            ...listOptions,
            sort: newSort
        });
        props.onSortChanged?.(newSort);
    }

    async function onButtonClick(button: string) {
        if (props.hideMenu) return;
        if (disabledButtons.includes(button)) {
            return;
        }
        if (button === "refresh") {
            await load(1);
        } else if (button === "add" || button === "view" || button === "edit" || button === "delete") {
            let model = button != "add" ? focusedItem : (props.newItemCreator ? props.newItemCreator() : {} as TItem);
            if (button == "edit" || button == "view") {
                model = await props.provider.getByID((model as any)[props.provider.keyProperty!] as string);
            }
            setDialogContext({state: button as AppListPageDialogContextState, model: {...model} as TItem});
        } else if (props.onButtonClick) {
            props.onButtonClick(button, focusedItem, () => loadData());
        }
    }

    const closeDialog = () => setDialogContext(undefined);

    async function onSaveClick()
    {

        if (dialogContext) {
            const validateResult = dialogContext!.validateCallback ? await dialogContext!.validateCallback() : true;
            const model = dialogContext.model as TItem;
            const id = (model as any)[props.provider.keyProperty];

            if (validateResult) {
                await setSaving(true);
                try {
                    try {
                        switch (dialogContext!.state) {
                            case AppListPageDialogContextState.add:
                                await (props.provider as DataProvider<TItem, TListOptions>).insert(model);
                                break;
                            case AppListPageDialogContextState.edit:
                                await (props.provider as DataProvider<TItem, TListOptions>).update(id, model);
                        }
                    } catch (e) {
                        alert(`catch ${e!.toString()}`);
                        return;
                    }
                    await loadData();
                } finally {
                    await setSaving(false);
                }
                closeDialog();
            }
        }
    }


    function getDialog(context: AppListPageDialogContext<TItem>) {
        const detailsComponent = props.getDetailsComponent ? props.getDetailsComponent!(context) : undefined;
        switch (context.state) {
            case AppListPageDialogContextState.add:
            case AppListPageDialogContextState.edit:
            case AppListPageDialogContextState.view:
                return <Modal
                    style={{width: "90%"}}
                    dimmer={"blurring"}
                    onClose={() => setDialogContext(undefined)}
                    open>
                    <Modal.Header>{props.getDetailsDialogTitle ? props.getDetailsDialogTitle(context.state!) : getDetailsDialogTitle(context.state)}</Modal.Header>
                    <Modal.Content>
                        {detailsComponent && detailsComponent}
                    </Modal.Content>
                    <Modal.Actions>
                        {context.state == AppListPageDialogContextState.view ? <>
                                <Button content={"Закрыть"} onClick={closeDialog}/>
                            </> :
                            <>
                                <Button content={"Сохранить"} disabled={saving} onClick={onSaveClick} color={"green"}/>
                                <Button content={"Отмена"} disabled={saving} onClick={closeDialog} color={"red"}/>
                            </>}
                    </Modal.Actions>
                </Modal>;
            case AppListPageDialogContextState.delete:
                return <AppListDeleteModal
                    onDelete={async () => {
                        try {
                            await (props.provider as DataProvider<TItem, TListOptions>).delete((context.model! as any)[props.provider.keyProperty]);
                            setFocusedItem(undefined);
                        } catch (e) {
                            alert(e);
                        }
                    }
                    }
                    onClose={async (success) => {
                        closeDialog();
                        if (success)
                            await load();
                    }}/>;
        }
        return <></>;
    }

    function onFilterChanged(value: Map<string, any>) {
        const newFilter = Object.fromEntries(value);
        const newListOptions = props.provider.applyFilter(listOptions, newFilter);
        if (filter) {
            for (const key of Object.keys(filter)) {
                if (!newFilter[key])
                    delete (newListOptions as any)[key];
            }
        }
        setListOptions(newListOptions);
        setFilter(newFilter);
        props.onFilterChanged?.(newFilter);
    }

    return <Container fluid>
        {dialogContext && getDialog(dialogContext)}

        {!props.hideMenu && <AppListMenu disabledButtons={disabledButtons}
                                         buttons={props.menuButtons}
                                         showSearchInput={!(props.hideSearch ?? false)}
                                         onSearchInputChanged={onSearchChanged}
                                         onButtonClick={onButtonClick}/>}
        {loadDataError && <h5>{loadDataError}</h5>}
        {!!items && <div>
            <AppListDataGrid<TItem>
                items={items}
                columns={props.columns}
                multiSelect={props.multiSelect}
                keyProperty={props.provider.keyProperty}
                onItemDoubleClick={() => onButtonClick("view")}
                focusedItem={focusedItem}
                onFocusedItemChange={setFocusedItem}
                onSortColumnsChanged={onSortColumnsChanged}
                onFilterChanged={onFilterChanged}
                selectedIds={props.selectedIds}
                height={props.gridHeight}
                onSelectionChanged={props.onSelectionChanged}/>
            {!(props.hidePaging ?? false) && paging &&
                <PagingFooter page={paging.page}
                              totalPages={paging.totalPages}
                              totalDocs={paging.totalDocs}
                              nextPage={paging.nextPage}
                              prevPage={paging.prevPage}
                              limit={paging.limit}
                              loadPage={load}/>}
        </div>}
    </Container>;
}

export default AppListPage;